import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Star, FileText, Award, Sparkles, TrendingUp, TrendingDown, Minus, Download, Loader2 } from "lucide-react";
import SendBriefModal from "@/components/SendBriefModal";
import AICopilotChat from "@/components/AICopilotChat";
import CalendarHoldModal from "@/components/CalendarHoldModal";
import BackgroundCheckModal from "@/components/BackgroundCheckModal";
import BackgroundCheckBadge from "@/components/BackgroundCheckBadge";
import AdverseActionCard from "@/components/AdverseActionCard";
import html2pdf from "html2pdf.js";

function ConfidenceTrajectory({ warmup, liveScore }) {
  const warmupScore = Number(warmup?.quality?.quality_score || 0);
  const final = Number(liveScore || 0);
  const delta = +(final - warmupScore).toFixed(1);
  const trend =
    delta > 1 ? "up" : delta < -1 ? "down" : "flat";
  const TrendIcon =
    trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendColor =
    trend === "up"
      ? "text-emerald-600"
      : trend === "down"
      ? "text-red-600"
      : "text-slate-500";
  const trendLabel =
    trend === "up"
      ? "Rose to the moment"
      : trend === "down"
      ? "Cracked under pressure"
      : "Steady performer";
  const wPct = Math.min(100, Math.max(0, (warmupScore / 10) * 100));
  const lPct = Math.min(100, Math.max(0, (final / 10) * 100));

  return (
    <section
      data-testid="confidence-trajectory"
      className="mt-10 grid gap-px bg-slate-200 lg:grid-cols-12"
    >
      <div className="bg-white p-6 lg:col-span-4">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
          <Sparkles className="h-3 w-3" />
          Confidence trajectory
        </div>
        <p className="mt-2 text-xs text-slate-500">
          How the candidate evolved from warm-up to live performance.
        </p>
        <div
          data-testid="trajectory-summary"
          className={`mt-5 flex items-center gap-3 ${trendColor}`}
        >
          <TrendIcon className="h-6 w-6" />
          <div>
            <div className="font-heading text-2xl font-semibold">
              {delta > 0 ? "+" : ""}
              {delta}
            </div>
            <div className="text-xs uppercase tracking-[0.2em]">
              {trendLabel}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 lg:col-span-8">
        <div className="space-y-5">
          <Bar
            label="Warm-up answer quality"
            value={warmupScore}
            pct={wPct}
            color="bg-slate-300"
            testId="trajectory-warmup-bar"
          />
          <Bar
            label="Live overall score"
            value={final}
            pct={lPct}
            color="bg-blue-700"
            testId="trajectory-live-bar"
          />
        </div>
        {warmup?.question && (
          <div className="mt-6 border-t border-slate-100 pt-4 text-sm">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              Warm-up question
            </div>
            <p className="mt-1 text-slate-700">{warmup.question}</p>
          </div>
        )}
        {warmup?.confidence && (
          <div className="mt-4 text-xs italic text-slate-500">
            “{warmup.confidence}”
          </div>
        )}
      </div>
    </section>
  );
}

function Bar({ label, value, pct, color, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-end justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <div className="kpi-stat font-heading text-2xl font-semibold">
          {value}
          <span className="text-sm text-slate-400">/10</span>
        </div>
      </div>
      <div className="mt-2 h-2 w-full bg-slate-100">
        <div
          className={`h-full ${color} transition-all duration-700`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


export default function Review() {
  const { jobId, interviewId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeQId, setActiveQId] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [rubricBusy, setRubricBusy] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [bgModalOpen, setBgModalOpen] = useState(false);
  const [resume, setResume] = useState(null);
  const [bgChecks, setBgChecks] = useState([]);
  const briefRef = useRef(null);

  // Resolve the resume row + any existing background checks for this candidate
  useEffect(() => {
    if (!interview?.candidate_email || !interview?.job_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/applicants", {
          params: {
            email: interview.candidate_email,
            job_id: interview.job_id,
          },
        });
        if (cancelled) return;
        const r =
          (data?.applicants || []).find(
            (x) =>
              x.candidate_email === interview.candidate_email &&
              x.job_id === interview.job_id
          ) || null;
        setResume(r);
        if (r) {
          const { data: bgData } = await api.get("/background-checks", {
            params: { resume_id: r.id },
          });
          if (!cancelled) setBgChecks(bgData?.checks || []);
        }
      } catch {
        // best-effort — Background Check button still renders with a "no resume" guard
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [interview?.candidate_email, interview?.job_id]);

  const runRubricCheck = async () => {
    if (!activeQId) return;
    setRubricBusy(true);
    try {
      const { data } = await api.post(
        `/interviews/${interviewId}/score-rubric`,
        { question_id: activeQId }
      );
      setInterview((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          answers: prev.answers.map((a) =>
            a.question_id === activeQId ? { ...a, rubric_check: data } : a
          ),
        };
      });
      toast.success("Rubric check ready");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not run the rubric check"
      );
    } finally {
      setRubricBusy(false);
    }
  };

  const exportBrief = async () => {
    if (!briefRef.current) return;
    setExportingPdf(true);
    try {
      const safeName = (interview?.candidate_name || "candidate")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      await html2pdf()
        .set({
          margin: [12, 12, 12, 12],
          filename: `hireloop-brief-${safeName}.pdf`,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(briefRef.current)
        .save();
      toast.success("Candidate brief downloaded");
    } catch (err) {
      toast.error("Could not generate PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    api
      .get(`/interviews/employer/${interviewId}`)
      .then(({ data }) => {
        setInterview(data);
        if (data.answers?.length) setActiveQId(data.answers[0].question_id);
      })
      .catch(() => {
        toast.error("Could not load interview");
        navigate(`/jobs/${jobId}`);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [interviewId]);

  // Fetch video blob for active question (live = single video; async = per question)
  useEffect(() => {
    if (!activeQId || !interview) return;
    let revoked = false;
    let currentUrl = null;
    const token = localStorage.getItem("aip_token");
    const isLive = interview.mode === "live";
    const url = isLive
      ? `${API}/live-video/${interviewId}`
      : `${API}/video/${interviewId}/${activeQId}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("video fetch failed");
        return r.blob();
      })
      .then((blob) => {
        if (revoked) return;
        currentUrl = URL.createObjectURL(blob);
        setBlobUrl(currentUrl);
      })
      .catch(() => setBlobUrl(null));
    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [activeQId, interviewId, interview?.mode]); // eslint-disable-line

  if (loading || !interview) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-12 text-slate-500">
        Loading…
      </main>
    );
  }

  const answers = interview.answers || [];
  const activeAnswer = answers.find((a) => a.question_id === activeQId);
  const job = interview.job;

  return (
    <main data-testid="review-page" className="mx-auto max-w-7xl px-6 py-12">
      <button
        data-testid="review-back-btn"
        onClick={() => navigate(`/jobs/${jobId}`)}
        className="mb-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-3 w-3" /> Back to role
      </button>

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
            Candidate review
          </div>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            {interview.candidate_name}
          </h1>
          <div className="mt-2 text-sm text-slate-500">
            {interview.candidate_email} · Applied for{" "}
            <span className="font-medium text-slate-900">{job?.title}</span>
          </div>
          {bgChecks.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="review-bg-badges">
              {bgChecks.slice(0, 3).map((bg) => (
                <BackgroundCheckBadge
                  key={bg.id}
                  status={bg.status}
                  assessment={bg.assessment}
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex items-stretch gap-3">
          <SendBriefModal
            interviewId={interviewId}
            candidateName={interview?.candidate_name || "this candidate"}
          />
          <Button
            type="button"
            data-testid="review-schedule-calendar-btn"
            variant="outline"
            onClick={() => setCalendarModalOpen(true)}
            className="rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Schedule on Calendar
          </Button>
          <Button
            type="button"
            data-testid="review-background-check-btn"
            variant="outline"
            onClick={() => {
              if (!resume) {
                toast.error("Candidate record not loaded yet — try again in a moment");
                return;
              }
              setBgModalOpen(true);
            }}
            className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Background check
          </Button>
          <Button
            type="button"
            data-testid="review-export-pdf-btn"
            variant="outline"
            disabled={exportingPdf}
            onClick={exportBrief}
            className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <Download className="mr-2 h-4 w-4" />
            {exportingPdf ? "Generating PDF…" : "Export candidate brief"}
          </Button>
          <div className="border border-slate-200 px-6 py-4 text-right">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Overall score
            </div>
            <div className="kpi-stat mt-1 font-heading text-5xl font-semibold text-blue-700">
              {interview.overall_score ?? "—"}
              <span className="text-lg text-slate-400">/10</span>
            </div>
            {interview.weighted_score != null &&
              interview.weighted_score !== interview.overall_score && (
                <div
                  data-testid="review-weighted-score"
                  title="Weighted by this job's scorecard rubric (Settings → Scorecard rubric)"
                  className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700"
                >
                  Weighted ·{" "}
                  <span className="font-semibold">
                    {interview.weighted_score.toFixed(1)}
                  </span>
                  /10
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Confidence trajectory — only for live interviews with warmup data */}
      {interview.mode === "live" && interview.warmup?.quality && (
        <ConfidenceTrajectory
          warmup={interview.warmup}
          liveScore={interview.overall_score ?? 0}
        />
      )}

      {/* Post-call gap analysis — surfaces what the candidate dodged / missed */}
      {interview.gap_analysis &&
        (interview.gap_analysis.gaps?.length > 0 ||
          interview.gap_analysis.overall_takeaway) && (
          <section
            data-testid="review-gap-analysis"
            className="mt-8 border border-amber-200 bg-amber-50 p-6"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                <span>AI gap analysis</span>
              </div>
              <span className="text-[10px] uppercase tracking-[0.16em] text-amber-600">
                {interview.gap_analysis.gaps?.length || 0} gap
                {(interview.gap_analysis.gaps?.length || 0) === 1 ? "" : "s"} flagged
              </span>
            </div>
            {interview.gap_analysis.overall_takeaway && (
              <p className="mt-3 text-sm leading-relaxed text-amber-900">
                <strong>Bottom line:</strong> {interview.gap_analysis.overall_takeaway}
              </p>
            )}
            {interview.gap_analysis.gaps?.length > 0 && (
              <ol className="mt-4 space-y-3">
                {interview.gap_analysis.gaps.map((g, i) => (
                  <li
                    key={i}
                    data-testid={`review-gap-${i}`}
                    className="border-l-2 border-amber-400 bg-white/60 p-3"
                  >
                    <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-800">
                      {g.area}
                    </div>
                    <p className="mt-1 text-sm text-slate-800">{g.detail}</p>
                    {g.suggested_followup && (
                      <p className="mt-2 text-sm text-slate-700">
                        <strong className="text-amber-700">Ask in round 2:</strong>{" "}
                        <em>&ldquo;{g.suggested_followup}&rdquo;</em>
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>
        )}

      {/* AI Co-pilot — ask anything about this candidate, grounded in transcripts */}
      <AICopilotChat interviewId={interview.id} />

      {/* Adverse Action workflow — only shows if a BG check returned "consider" */}
      {bgChecks
        .filter((bg) => bg.assessment === "consider")
        .map((bg) => (
          <AdverseActionCard
            key={bg.id}
            bgCheck={bg}
            onChanged={async () => {
              if (!resume) return;
              try {
                const { data: bgData } = await api.get("/background-checks", {
                  params: { resume_id: resume.id },
                });
                setBgChecks(bgData?.checks || []);
              } catch {
                // silent — UI keeps stale state
              }
            }}
          />
        ))}

      {/* Recruiter notes summary — from in-call interviewer notes */}
      {interview.recruiter_notes_summary && (
        <section
          data-testid="review-notes-summary"
          className="mt-8 border border-violet-200 bg-violet-50 p-6"
        >
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-violet-700">
            Recruiter notes — AI summary
          </div>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700">
                Strengths
              </div>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-sm text-slate-800">
                {(interview.recruiter_notes_summary.strengths || []).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-rose-700">
                Concerns
              </div>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-sm text-slate-800">
                {(interview.recruiter_notes_summary.concerns || []).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700">
                Next step
              </div>
              <p className="mt-1 text-sm text-slate-800">
                {interview.recruiter_notes_summary.next_step}
              </p>
            </div>
          </div>
        </section>
      )}

      <div className="mt-10 grid gap-px bg-slate-200 lg:grid-cols-12">
        {/* Question list */}
        <aside className="bg-white p-6 lg:col-span-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Questions
          </div>
          <ol className="mt-4 space-y-2">
            {answers.length === 0 && (
              <div
                data-testid="review-empty"
                className="border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500"
              >
                No answers submitted yet.
              </div>
            )}
            {answers.map((a, i) => (
              <li key={a.id}>
                <button
                  data-testid={`review-question-btn-${i}`}
                  onClick={() => setActiveQId(a.question_id)}
                  className={`flex w-full items-start justify-between gap-3 border p-3 text-left text-sm transition-colors ${
                    activeQId === a.question_id
                      ? "border-blue-700 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div>
                    <div className="font-mono text-xs text-blue-700">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1 line-clamp-2">{a.question_text}</div>
                  </div>
                  <Badge
                    variant="outline"
                    className="rounded-sm border-slate-200 font-mono text-sm"
                  >
                    {a.score}/10
                  </Badge>
                </button>
              </li>
            ))}
          </ol>
        </aside>

        {/* Detail */}
        <section className="bg-white p-6 lg:col-span-8">
          {!activeAnswer ? (
            <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-slate-500">
              Select a question to review the answer.
            </div>
          ) : (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
                Answer
              </div>
              <p className="mt-2 font-heading text-lg font-medium leading-snug">
                {activeAnswer.question_text}
              </p>
              {(() => {
                const q = (interview?.job?.questions || []).find(
                  (qq) => qq.id === activeQId
                );
                if (!q?.listen_for) return null;
                return (
                  <p
                    data-testid="review-listen-for"
                    className="mt-2 border-l-2 border-amber-300 bg-amber-50/50 pl-3 text-xs italic leading-relaxed text-slate-700"
                  >
                    <strong className="not-italic text-amber-700">
                      Listen for:
                    </strong>{" "}
                    {q.listen_for}
                  </p>
                );
              })()}

              <div className="mt-4 overflow-hidden border border-slate-200 bg-black">
                {blobUrl ? (
                  <video
                    data-testid="review-video"
                    src={blobUrl}
                    controls
                    className="aspect-video w-full bg-black"
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center text-sm text-slate-500">
                    Loading video…
                  </div>
                )}
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <div className="border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                    <Star className="h-3 w-3" /> Score
                  </div>
                  <div className="kpi-stat mt-2 font-heading text-3xl font-semibold text-blue-700">
                    {activeAnswer.score}
                    <span className="text-base text-slate-400">/10</span>
                  </div>
                </div>
                <div className="border border-slate-200 p-4 sm:col-span-2">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                    <Award className="h-3 w-3" /> AI feedback
                  </div>
                  <p className="mt-2 text-sm leading-relaxed">
                    {activeAnswer.feedback || "—"}
                  </p>
                </div>
              </div>

              {(() => {
                const q = (interview?.job?.questions || []).find(
                  (qq) => qq.id === activeQId
                );
                const rubric = activeAnswer.rubric_check;
                if (!q?.listen_for) return null;
                return (
                  <div
                    data-testid="review-rubric-block"
                    className="mt-6 border border-amber-200 bg-amber-50/30 p-5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                        <Sparkles className="h-3 w-3" /> AI Rubric Check
                      </div>
                      {!rubric && (
                        <button
                          type="button"
                          data-testid="review-rubric-run-btn"
                          onClick={runRubricCheck}
                          disabled={rubricBusy || !activeAnswer.transcript}
                          className="rounded-sm border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                        >
                          {rubricBusy ? (
                            <>
                              <Loader2 className="mr-2 inline h-3 w-3 animate-spin" />
                              Scoring…
                            </>
                          ) : (
                            "Score against rubric"
                          )}
                        </button>
                      )}
                    </div>
                    {rubric ? (
                      <>
                        <ul
                          data-testid="review-rubric-signals"
                          className="mt-3 space-y-1.5"
                        >
                          {rubric.signals.map((s, idx) => {
                            const palette =
                              s.status === "hit"
                                ? "text-emerald-700"
                                : s.status === "partial"
                                ? "text-amber-700"
                                : "text-red-700";
                            const icon =
                              s.status === "hit" ? "✓" : s.status === "miss" ? "✗" : "~";
                            return (
                              <li
                                key={idx}
                                data-testid={`review-rubric-signal-${idx}`}
                                data-status={s.status}
                                className={`flex items-start gap-2 text-sm ${palette}`}
                              >
                                <span className="font-bold">{icon}</span>
                                <span className="text-slate-800">{s.label}</span>
                              </li>
                            );
                          })}
                        </ul>
                        {rubric.verdict && (
                          <p className="mt-3 border-t border-amber-200 pt-3 text-sm italic text-slate-700">
                            <strong className="not-italic text-slate-900">Verdict:</strong>{" "}
                            {rubric.verdict}
                          </p>
                        )}
                        <button
                          type="button"
                          data-testid="review-rubric-rerun-btn"
                          onClick={runRubricCheck}
                          disabled={rubricBusy}
                          className="mt-3 text-[11px] font-medium text-amber-700 hover:underline disabled:opacity-50"
                        >
                          {rubricBusy ? "Re-scoring…" : "Re-score with latest model"}
                        </button>
                      </>
                    ) : (
                      <p className="mt-2 text-xs italic text-slate-600">
                        Run an AI check to see ✓/✗ marks for each rubric signal —
                        ideal for hiring-manager hand-offs.
                      </p>
                    )}
                  </div>
                );
              })()}

              <div className="mt-6 border border-slate-200 p-5">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                  <FileText className="h-3 w-3" /> Transcript
                </div>
                <p
                  data-testid="review-transcript"
                  className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700"
                >
                  {activeAnswer.transcript || "[Empty transcript]"}
                </p>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Hidden printable brief — rendered off-screen, exported as PDF by html2pdf */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-99999px",
          top: 0,
          width: "794px",
        }}
      >
        <div
          ref={briefRef}
          style={{
            width: "794px",
            padding: "40px",
            fontFamily: "Inter, -apple-system, sans-serif",
            color: "#0f172a",
            background: "#ffffff",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              borderBottom: "2px solid #1d4ed8",
              paddingBottom: "16px",
              marginBottom: "24px",
            }}
          >
            <div>
              {user?.logo_url && (
                <img
                  src={user.logo_url}
                  alt=""
                  style={{ height: 36, maxWidth: 180, objectFit: "contain", marginBottom: 8 }}
                />
              )}
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: "#1d4ed8", textTransform: "uppercase" }}>
                Candidate Brief
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                {interview?.candidate_name || "—"}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                {interview?.candidate_email}
              </div>
              <div style={{ fontSize: 12, color: "#334155", marginTop: 2 }}>
                Role: <strong>{job?.title}</strong> ·{" "}
                {(user?.brand_company_label || user?.company || "")}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#64748b", textTransform: "uppercase" }}>
                Overall score
              </div>
              <div style={{ fontSize: 38, fontWeight: 700, color: "#1d4ed8", marginTop: 2, lineHeight: 1 }}>
                {interview?.overall_score ?? "—"}
                <span style={{ fontSize: 14, color: "#94a3b8" }}>/10</span>
              </div>
            </div>
          </div>

          {/* Recruiter Notes Summary */}
          {interview?.recruiter_notes_summary && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#047857", textTransform: "uppercase", marginBottom: 6 }}>
                    Strengths
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.5 }}>
                    {(interview.recruiter_notes_summary.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#b91c1c", textTransform: "uppercase", marginBottom: 6 }}>
                    Concerns
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.5 }}>
                    {(interview.recruiter_notes_summary.concerns || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#1d4ed8", textTransform: "uppercase", marginBottom: 6 }}>
                    Recommended next step
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                    {interview.recruiter_notes_summary.next_step}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Gap Analysis */}
          {interview?.gap_analysis?.gaps?.length > 0 && (
            <div style={{ marginBottom: 24, borderTop: "1px solid #e2e8f0", paddingTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#b45309", textTransform: "uppercase", marginBottom: 10 }}>
                AI Gap Analysis · {interview.gap_analysis.gaps.length} flagged
              </div>
              {interview.gap_analysis.overall_takeaway && (
                <p style={{ fontSize: 12, color: "#78350f", marginTop: 0, marginBottom: 12, fontStyle: "italic" }}>
                  <strong>Bottom line:</strong> {interview.gap_analysis.overall_takeaway}
                </p>
              )}
              {interview.gap_analysis.gaps.map((g, i) => (
                <div key={i} style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10, marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: 1.2 }}>
                    {g.area}
                  </div>
                  <p style={{ fontSize: 12, margin: "4px 0 6px 0", lineHeight: 1.5 }}>{g.detail}</p>
                  {g.suggested_followup && (
                    <div style={{ fontSize: 11, color: "#1e293b", fontStyle: "italic" }}>
                      <strong style={{ color: "#b45309", fontStyle: "normal" }}>Ask in round 2:</strong>{" "}
                      &ldquo;{g.suggested_followup}&rdquo;
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Per-question scores */}
          {interview?.answers?.length > 0 && (
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#475569", textTransform: "uppercase", marginBottom: 10 }}>
                Per-question scores
              </div>
              {interview.answers.map((a, i) => {
                const rubric = a.rubric_check;
                const q = (interview?.job?.questions || []).find(
                  (qq) => qq.id === a.question_id
                );
                return (
                  <div
                    key={a.question_id || i}
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px dashed #e2e8f0",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span style={{ flex: 1 }}>
                        Q{i + 1}: {(a.question_text || "").slice(0, 80)}
                        {(a.question_text || "").length > 80 ? "…" : ""}
                      </span>
                      <strong
                        style={{
                          color:
                            a.score >= 7
                              ? "#047857"
                              : a.score >= 4
                              ? "#b45309"
                              : "#b91c1c",
                        }}
                      >
                        {a.score?.toFixed?.(1) ?? a.score ?? "—"}/10
                      </strong>
                    </div>
                    {q?.listen_for && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 10,
                          color: "#92400e",
                          fontStyle: "italic",
                          borderLeft: "2px solid #fcd34d",
                          paddingLeft: 8,
                        }}
                      >
                        Listen for: {q.listen_for}
                      </div>
                    )}
                    {rubric?.signals?.length > 0 && (
                      <div style={{ marginTop: 6, marginLeft: 12 }}>
                        {rubric.signals.map((s, idx) => {
                          const symbol =
                            s.status === "hit"
                              ? "✓"
                              : s.status === "miss"
                              ? "✗"
                              : "~";
                          const color =
                            s.status === "hit"
                              ? "#047857"
                              : s.status === "miss"
                              ? "#b91c1c"
                              : "#b45309";
                          return (
                            <div
                              key={idx}
                              style={{
                                fontSize: 10,
                                color,
                                lineHeight: 1.5,
                              }}
                            >
                              <strong>{symbol}</strong>{" "}
                              <span style={{ color: "#334155" }}>{s.label}</span>
                            </div>
                          );
                        })}
                        {rubric.verdict && (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 10,
                              color: "#475569",
                              fontStyle: "italic",
                            }}
                          >
                            <strong style={{ fontStyle: "normal", color: "#0f172a" }}>
                              Verdict:
                            </strong>{" "}
                            {rubric.verdict}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div style={{ marginTop: 30, paddingTop: 12, borderTop: "1px solid #e2e8f0", fontSize: 9, color: "#94a3b8", display: "flex", justifyContent: "space-between" }}>
            <span>Generated {new Date().toLocaleString()}</span>
            {!user?.hide_powered_by && <span>Powered by Hireloop PRO</span>}
          </div>
        </div>
      </div>
      <CalendarHoldModal
        open={calendarModalOpen}
        onOpenChange={setCalendarModalOpen}
        interviewId={interviewId}
        candidateName={interview?.candidate_name || ""}
        candidateEmail={interview?.candidate_email || ""}
        jobTitle={job?.title || ""}
        defaultRecruiterEmail={user?.email || ""}
      />
      <BackgroundCheckModal
        open={bgModalOpen}
        onOpenChange={setBgModalOpen}
        resumeId={resume?.id}
        candidateName={interview?.candidate_name || ""}
        candidateEmail={interview?.candidate_email || ""}
        onOrdered={(bg) => setBgChecks((prev) => [bg, ...prev])}
      />
    </main>
  );
}
